/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useState } from 'react';

import { Box, IconButton, OutlinedInput, Stack, Tooltip, Typography } from '@mui/material';

import {
  FitScreenOutlined as FitScreenOutlinedIcon,
  HeightOutlined as HeightOutlinedIcon,
  RedoOutlined as RedoOutlinedIcon,
  SaveOutlined as SaveOutlinedIcon,
  UndoOutlined as UndoOutlinedIcon,
  WidthFullOutlined as WidthFullOutlinedIcon,
  ZoomInOutlined as ZoomInOutlinedIcon,
  ZoomOutOutlined as ZoomOutOutlinedIcon,
} from '@mui/icons-material';

import { ArtworkContext } from 'contexts/ArtworkContext';
import { useArtworkDrawers } from 'hooks/useArtworkDrawers';

export const ToolBar = () => {
  const { openDrawerSaveArtwork } = useArtworkDrawers();

  const { callbacks, chiliDoc, publisher } = useContext(ArtworkContext);
  const { handleFit, handleUndo, handleZoom } = callbacks;

  const [zoomValue, setZoomValue] = useState(null);
  const [disableUndo, setDisableUndo] = useState(true);
  const [disableRedo, setDisableRedo] = useState(true);

  const checkUndoManager = async () => {
    const checkUndoManager = await publisher.getObject('document.undoManager');
    setDisableUndo((checkUndoManager?.undoStack?.match(/\d+/)?.[0] ?? 0) < 1);
    setDisableRedo((checkUndoManager?.redoStack?.match(/\d+/)?.[0] ?? 0) < 1);
  };

  const handleBlur = () => {
    handleZoom(zoomValue);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleZoom(zoomValue);
    }
  };

  useEffect(() => {
    setZoomValue(chiliDoc?.zoom || 0);
    if (chiliDoc && publisher) {
      checkUndoManager();
    }
  }, [chiliDoc, publisher]);

  return (
    <Box
      sx={{
        width: '100%',
        height: '40px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '0 5px',
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
      >
        <Tooltip
          title="Undo"
          placement="bottom"
        >
          <IconButton
            disabled={!chiliDoc || disableUndo}
            onClick={() => handleUndo('Undo')}
          >
            <UndoOutlinedIcon sx={{ fontSize: 23 }} />
          </IconButton>
        </Tooltip>
        <Tooltip
          title="Redo"
          placement="bottom"
        >
          <IconButton
            disabled={!chiliDoc || disableRedo}
            onClick={() => handleUndo('Redo')}
          >
            <RedoOutlinedIcon sx={{ fontSize: 23 }} />
          </IconButton>
        </Tooltip>
        <Tooltip
          title="Fit to page width"
          placement="bottom"
        >
          <IconButton
            disabled={!chiliDoc}
            onClick={() => handleFit('width')}
          >
            <WidthFullOutlinedIcon sx={{ fontSize: 23 }} />
          </IconButton>
        </Tooltip>
        <Tooltip
          title="Fit to page height"
          placement="bottom"
        >
          <IconButton
            disabled={!chiliDoc}
            onClick={() => handleFit('height')}
          >
            <HeightOutlinedIcon sx={{ fontSize: 23 }} />
          </IconButton>
        </Tooltip>
        <Tooltip
          title="Fit to page"
          placement="bottom"
        >
          <IconButton
            disabled={!chiliDoc}
            onClick={() => handleFit('page')}
          >
            <FitScreenOutlinedIcon sx={{ fontSize: 23 }} />
          </IconButton>
        </Tooltip>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
        >
          <Typography
            variant="body2"
            fontWeight={500}
          >
            Zoom:
          </Typography>
          <OutlinedInput
            size="small"
            value={zoomValue}
            onChange={(e) => setZoomValue(e.target.value)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            disabled={!chiliDoc}
            sx={{
              height: 28,
              fontSize: '0.75rem',
              px: 0,
              borderRadius: '3px',
              width: '50px',
              '& input': {
                padding: '0px',
                textAlign: 'center',
              },
            }}
          />
        </Stack>
        <Tooltip
          title="Zoom Out"
          placement="bottom"
        >
          <IconButton
            disabled={!chiliDoc}
            onClick={() => handleZoom('out')}
          >
            <ZoomOutOutlinedIcon sx={{ fontSize: 23 }} />
          </IconButton>
        </Tooltip>
        <Tooltip
          title="Zoom In"
          placement="bottom"
        >
          <IconButton
            disabled={!chiliDoc}
            onClick={() => handleZoom('in')}
          >
            <ZoomInOutlinedIcon sx={{ fontSize: 23 }} />
          </IconButton>
        </Tooltip>
      </Stack>
      <Tooltip
        title="Save"
        placement="bottom"
      >
        <IconButton
          disabled={!chiliDoc}
          onClick={openDrawerSaveArtwork}
        >
          <SaveOutlinedIcon sx={{ fontSize: 23 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
};
